/* eslint-disable react/no-array-index-key */
import { SuperFeatureProperty } from '@propertypal/shared/src/types/superFeature';
import React, { Dispatch, FunctionComponent, useEffect, useRef, useState, MutableRefObject } from 'react';
import { useTheme } from 'styled-components';
import { AccountLogo, SliderTextBox } from '../home/HomeHeader.style';
import PropertyPrice from '../property/PropertyPrice';
import { Heading } from '../typography';
import { Container, Wrapper, Content, ImageBox, ImageLink, ImagePropertyDetails } from './ImageSlideshow.style.pn';

interface Props {
  images: string[];
  highResImages?: string[];
  slideDuration: number;
  superFeatureProperties: SuperFeatureProperty[];
  highResThreshold?: number;
}

const animateNextSlide =
  (
    lastSlide: number,
    setActiveIndex: Dispatch<number>,
    images: string[],
    slideDuration: number,
    timeout: MutableRefObject<any>,
  ) =>
  () => {
    const nextSlide = lastSlide + 1 < images.length ? lastSlide + 1 : 0;

    setActiveIndex(nextSlide);

    timeout.current = setTimeout(
      animateNextSlide(nextSlide, setActiveIndex, images, slideDuration, timeout),
      slideDuration,
    );
  };

const clearCurrentTimeout = (timeout: MutableRefObject<any>) => {
  if (timeout.current) {
    clearTimeout(timeout.current);
  }
};

const ImageSlideshowPn: FunctionComponent<Props> = (props) => {
  const theme = useTheme();
  const timeout = useRef<any>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const lastIndex = activeIndex - 1 < 0 ? props.images.length - 1 : activeIndex - 1;
  const nextIndex = activeIndex + 1 >= props.images.length ? 0 : activeIndex + 1;

  useEffect(() => {
    clearCurrentTimeout(timeout);

    timeout.current = setTimeout(
      animateNextSlide(activeIndex, setActiveIndex, props.images, props.slideDuration, timeout),
      props.slideDuration,
    );

    return () => clearCurrentTimeout(timeout);
  }, [props.images]);

  return (
    <Container>
      <Heading align="center" mb={10}>
        Search and Buy New Homes from Northern Ireland&apos;s Leading Developers
      </Heading>
      <Wrapper>
        <Content>
          <ImageLink key={lastIndex} href={props.superFeatureProperties[lastIndex]?.path} $active={false}>
            <ImageBox
              data-testid="slide-image"
              active={false}
              src={props.images[lastIndex]}
              sizes="(max-width: 1200px) 1024px, 1600px"
              alt={props.superFeatureProperties[lastIndex]?.displayAddress}
              srcSet={
                props.highResImages
                  ? `${props.images[lastIndex]} 1024w, ${props.highResImages[lastIndex]} ${props.highResThreshold}w`
                  : undefined
              }
            />
          </ImageLink>

          <ImageLink key={activeIndex} href={props.superFeatureProperties[activeIndex]?.path} $active>
            <ImageBox
              data-testid="slide-image"
              active
              src={props.images[activeIndex]}
              sizes="(max-width: 1200px) 1024px, 1600px"
              alt={props.superFeatureProperties[activeIndex]?.displayAddress}
              srcSet={
                props.highResImages
                  ? `${props.images[activeIndex]} 1024w, ${props.highResImages[activeIndex]} ${props.highResThreshold}w`
                  : undefined
              }
            />
          </ImageLink>

          <ImageLink key={nextIndex} href={props.superFeatureProperties[nextIndex]?.path} $active={false}>
            <ImageBox
              data-testid="slide-image"
              active={false}
              src={props.images[nextIndex]}
              sizes="(max-width: 1200px) 1024px, 1600px"
              alt={props.superFeatureProperties[nextIndex]?.displayAddress}
              srcSet={
                props.highResImages
                  ? `${props.images[nextIndex]} 1024w, ${props.highResImages[nextIndex]} ${props.highResThreshold}w`
                  : undefined
              }
            />
          </ImageLink>

          {props.superFeatureProperties.map((item, index) => {
            if (!item.path) return null;
            return (
              <ImagePropertyDetails key={item.id} active={index === activeIndex}>
                {item?.listingLogos?.map((logo) => (
                  <AccountLogo
                    style={{
                      backgroundImage: `url(${logo.standard})`,
                      backgroundColor: logo.standardBackgroundColour,
                    }}
                  />
                ))}

                <SliderTextBox href={item.path}>
                  {item.displayAddress}
                  {item.price && (
                    <>
                      {' - '}
                      <PropertyPrice
                        as="span"
                        price={item.price}
                        fontSize={16}
                        primaryColor={theme.textDark}
                        secondaryColor={theme.textDark}
                      />
                    </>
                  )}
                </SliderTextBox>
              </ImagePropertyDetails>
            );
          })}
        </Content>
      </Wrapper>
    </Container>
  );
};

export default ImageSlideshowPn;
